import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Bot,
  Copy,
  Download,
  Eye,
  EyeOff,
  MailX,
  MousePointerClick,
  Plus,
  Radio,
  Trash2,
  Upload,
  Users,
} from "lucide-react";

import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  createCampaign,
  deleteCampaign,
  getCampaignDetail,
  importRecipients,
  listCampaigns,
  updateCampaign,
} from "@/lib/outreach-tracking.functions";

export const Route = createFileRoute("/_authenticated/outreach-tracking")({
  component: OutreachTrackingPage,
  validateSearch: (s: Record<string, unknown>) => ({
    id: typeof s.id === "string" ? s.id : undefined,
  }),
  head: () => ({ meta: [{ title: "Outreach Tracking — InboxPulse" }] }),
});

function OutreachTrackingPage() {
  const { id } = Route.useSearch();
  return id ? <CampaignDetail id={id} /> : <CampaignList />;
}

/* -----------------------------------------------------------------
 * LIST VIEW
 * ----------------------------------------------------------------- */

function CampaignList() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const listFn = useServerFn(listCampaigns);
  const createFn = useServerFn(createCampaign);

  const { data, isLoading } = useQuery({
    queryKey: ["ot-campaigns"],
    queryFn: () => listFn(),
  });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [domain, setDomain] = useState("");
  const [pixel, setPixel] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          name,
          description: desc || undefined,
          sending_domain: domain || null,
          pixel_enabled: pixel,
        },
      }),
    onSuccess: (res) => {
      toast.success("Kampagne erstellt");
      setOpen(false);
      setName("");
      setDesc("");
      setDomain("");
      setPixel(false);
      qc.invalidateQueries({ queryKey: ["ot-campaigns"] });
      nav({ to: "/outreach-tracking", search: { id: res.campaign.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const campaigns = data?.campaigns ?? [];

  return (
    <div className="p-8">
      <PageHeader
        title="Outreach Tracking"
        description="Tool-unabhängiges Link- und Open-Tracking für Cold-Outreach mit Bot-Filter und One-Click-Unsubscribe."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[image:var(--gradient-primary)] hover:opacity-90">
                <Plus className="size-4 mr-1" /> Neue Kampagne
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kampagne erstellen</DialogTitle>
                <DialogDescription>
                  Eine Kampagne bündelt Empfänger mit eindeutigen Tracking-Tokens.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="z. B. Outreach KW 21 — Steuerberater"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Beschreibung (optional)</Label>
                  <Input
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="z. B. Plusvibe-Sequenz #4"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Versand-Domain (optional)</Label>
                  <Input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="z. B. mein-angebot.de"
                  />
                  <p className="text-xs text-muted-foreground">
                    Hilft, Health pro Domain zu vergleichen wenn du rotierst.
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Tracking-Pixel</Label>
                    <p className="text-xs text-muted-foreground">
                      Open-Tracking. Bei Cold Outreach empfohlen: AUS.
                    </p>
                  </div>
                  <Switch checked={pixel} onCheckedChange={setPixel} />
                </div>
                <Button
                  onClick={() => create.mutate()}
                  disabled={!name || create.isPending}
                  className="w-full"
                >
                  {create.isPending ? "Erstelle…" : "Erstellen"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <HelpPanel
        title="Hilfe: Outreach-Tracking"
        what={
          <>
            Das Outreach-Tracking misst tool-unabhangig, was nach dem Versand mit deinen Cold-Mails
            passiert: wer offnet, wer klickt, wer abbestellt, wer beschwert sich. Jede
            Empfangeradresse bekommt ein eindeutiges Token in Links und (optional) Tracking-Pixel.
          </>
        }
        why={
          <>
            Du erkennst sofort, ob deine Outreach-Sequenz funktioniert - ohne dich auf die (oft
            ungenauen) Statistiken deines Versandtools verlassen zu mussen. Bot-Klicks werden
            automatisch herausgefiltert, damit Zahlen ehrlich bleiben.
          </>
        }
        how={[
          <>
            Lege oben rechts eine <b>neue Kampagne</b> an (z.B. "Outreach KW 21 - Steuerberater").
          </>,
          <>
            Importiere oder fuge Empfanger hinzu - das System erzeugt pro Empfanger Tracking-Links
            und Unsubscribe-Token.
          </>,
          <>
            Kopiere die personalisierten Links/Pixel-URLs in dein Versandtool (Lemlist, Instantly,
            eigene SMTP, ...).
          </>,
          <>
            Versende die Sequenz. Sobald Empfanger offnen oder klicken, fullen sich die Kennzahlen
            live.
          </>,
          <>
            Beobachte CTR, Unsubscribe-Rate und Bot-Anteil. Pausiere oder optimiere Schritte, die
            schlecht laufen.
          </>,
        ]}
        tips={[
          <>
            Aktiviere das Tracking-Pixel nur, wenn dein Versandtool HTML-Mails erlaubt - bei
            Plain-Text nutzt nur Link-Tracking.
          </>,
          <>
            Ein hoher Bot-Anteil (uber 50%) ist bei Cold-Outreach normal (Sicherheits-Scanner).
            Verlasse dich auf "echte" Klicks.
          </>,
          <>
            Unsubscribe-Rate uber 2% = Zielgruppe oder Botschaft passen nicht. Anpassen statt
            weitermachen.
          </>,
          <>Bounces fruh aus der Liste entfernen - sonst leidet deine Domain-Reputation.</>,
          <>
            Beschwerden (Spam-Reports) sind das gefahrlichste Signal: schon 0,1% konnen den Versand
            drosseln.
          </>,
        ]}
        glossary={[
          {
            term: "Offnung",
            def: (
              <>
                Tracking-Pixel wurde geladen. <i>Beispiel:</i> Empfanger offnet die Mail in Gmail -
                Pixel ladt - Open zahlt +1.
              </>
            ),
          },
          {
            term: "Klick",
            def: (
              <>
                Empfanger hat einen Tracking-Link angeklickt. <i>Beispiel:</i> Klick auf "Termin
                buchen" - Redirect uber unseren Server - Klick zahlt +1.
              </>
            ),
          },
          {
            term: "Bot-Klick",
            def: (
              <>
                Automatischer Klick durch Spamfilter/Scanner. <i>Beispiel:</i> Microsoft Defender
                klickt 0,3 s nach Zustellung alle Links - wird gefiltert.
              </>
            ),
          },
          {
            term: "Bounce",
            def: (
              <>
                Mail kam zuruck. <i>Beispiel:</i> "550 user unknown" - Adresse existiert nicht,
                Hard-Bounce.
              </>
            ),
          },
          {
            term: "Beschwerde (Complaint)",
            def: (
              <>
                Empfanger klickt "Spam melden". <i>Beispiel:</i> Gmail-Nutzer markiert deine Mail
                als Spam - Provider meldet das via Feedback-Loop.
              </>
            ),
          },
          {
            term: "Unsubscribe",
            def: (
              <>
                Empfanger nutzt den Abmelde-Link. <i>Beispiel:</i> Klick auf "Abmelden" - Token wird
                auf Suppression-Liste gesetzt.
              </>
            ),
          },
          {
            term: "CTR",
            def: "Click-Through-Rate: Klicks geteilt durch Empfanger, in %. Gute Cold-Outreach-CTR: 3-8%.",
          },
          {
            term: "Token",
            def: "Eindeutige Zeichenkette pro Empfanger in Link/Pixel - dadurch wissen wir, wer geklickt hat.",
          },
        ]}
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Lade…</p>
      ) : campaigns.length === 0 ? (
        <Card className="p-8 text-center">
          <Radio className="size-8 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold mb-1">Noch keine Kampagne</h3>
          <p className="text-sm text-muted-foreground">
            Erstelle deine erste Kampagne, um Tracking-Links zu generieren.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {campaigns.map((c) => {
            const ctr =
              c.total_recipients > 0
                ? ((c.total_clicks / c.total_recipients) * 100).toFixed(1)
                : "0";
            const unsubRate =
              c.total_recipients > 0
                ? ((c.total_unsubscribes / c.total_recipients) * 100).toFixed(2)
                : "0";
            return (
              <Card
                key={c.id}
                onClick={() => nav({ to: "/outreach-tracking", search: { id: c.id } })}
                className="p-4 hover:bg-accent/40 cursor-pointer transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{c.name}</h3>
                      <Badge variant={c.status === "active" ? "default" : "secondary"}>
                        {c.status}
                      </Badge>
                      {c.pixel_enabled && (
                        <Badge variant="outline" className="text-[10px]">
                          <Eye className="size-3 mr-1" /> Pixel
                        </Badge>
                      )}
                    </div>
                    {c.sending_domain && (
                      <p className="text-xs text-muted-foreground mb-2">von {c.sending_domain}</p>
                    )}
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        <Users className="size-3 inline mr-1" /> {c.total_recipients} Empfänger
                      </span>
                      <span>
                        <MousePointerClick className="size-3 inline mr-1" /> {c.total_clicks} Klicks
                        ({ctr}%)
                      </span>
                      <span>
                        <Bot className="size-3 inline mr-1" /> {c.total_bot_clicks} Bots
                      </span>
                      <span>
                        <MailX className="size-3 inline mr-1" /> {c.total_unsubscribes} Unsub (
                        {unsubRate}%)
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------
 * DETAIL VIEW
 * ----------------------------------------------------------------- */

type CsvRow = {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  target_url?: string | null;
  custom: Record<string, string>;
};

function parseCsv(text: string): { rows: CsvRow[]; errors: string[] } {
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { rows: [], errors: ["CSV braucht Header + mindestens 1 Zeile"] };

  const splitLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = !inQ;
      } else if ((ch === "," || ch === ";") && !inQ) {
        out.push(cur);
        cur = "";
      } else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const header = splitLine(lines[0]).map((h) => h.toLowerCase());
  const emailIdx = header.findIndex((h) => h === "email" || h === "e-mail");
  if (emailIdx === -1) {
    return { rows: [], errors: ['Header muss eine Spalte "email" enthalten'] };
  }
  const fnIdx = header.findIndex((h) => ["first_name", "firstname", "vorname"].includes(h));
  const lnIdx = header.findIndex((h) => ["last_name", "lastname", "nachname"].includes(h));
  const urlIdx = header.findIndex((h) => ["target_url", "url", "ziel"].includes(h));

  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitLine(lines[i]);
    const email = cols[emailIdx];
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push(`Zeile ${i + 1}: ungültige Email "${email}"`);
      continue;
    }
    const custom: Record<string, string> = {};
    header.forEach((h, idx) => {
      if (idx === emailIdx || idx === fnIdx || idx === lnIdx || idx === urlIdx) return;
      const v = cols[idx];
      if (v) custom[h] = v;
    });
    rows.push({
      email,
      first_name: fnIdx >= 0 ? cols[fnIdx] || null : null,
      last_name: lnIdx >= 0 ? cols[lnIdx] || null : null,
      target_url: urlIdx >= 0 ? cols[urlIdx] || null : null,
      custom,
    });
  }
  return { rows, errors };
}

function CampaignDetail({ id }: { id: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const detailFn = useServerFn(getCampaignDetail);
  const importFn = useServerFn(importRecipients);
  const updateFn = useServerFn(updateCampaign);
  const deleteFn = useServerFn(deleteCampaign);

  const { data, isLoading } = useQuery({
    queryKey: ["ot-campaign", id],
    queryFn: () => detailFn({ data: { id } }),
  });

  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");

  const parsed = useMemo(() => (csvText ? parseCsv(csvText) : null), [csvText]);

  const doImport = useMutation({
    mutationFn: () => {
      if (!parsed || parsed.rows.length === 0) throw new Error("Keine Zeilen");
      return importFn({
        data: {
          campaign_id: id,
          recipients: parsed.rows.map((r) => ({
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name,
            target_url: r.target_url,
            custom_fields: r.custom,
          })),
        },
      });
    },
    onSuccess: (res) => {
      toast.success(`${res.inserted} Empfänger importiert`);
      setImportOpen(false);
      setCsvText("");
      qc.invalidateQueries({ queryKey: ["ot-campaign", id] });
      qc.invalidateQueries({ queryKey: ["ot-campaigns"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePixel = useMutation({
    mutationFn: (val: boolean) => updateFn({ data: { id, pixel_enabled: val } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ot-campaign", id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const doDelete = useMutation({
    mutationFn: () => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Kampagne gelöscht");
      nav({ to: "/outreach-tracking", search: { id: undefined } });
      qc.invalidateQueries({ queryKey: ["ot-campaigns"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) {
    return <div className="p-8 text-sm text-muted-foreground">Lade…</div>;
  }

  const { campaign, recipients, clicks, opens, unsubscribes, tracking_base } = data;
  const base = tracking_base;

  const exportCsv = () => {
    const rows = recipients.map((r) => {
      const trackUrl = `${base}/api/public/r/${r.token}`;
      const unsubUrl = `${base}/api/public/u/${r.token}`;
      const pixelUrl = campaign.pixel_enabled ? `${base}/api/public/p/${r.token}.gif` : "";
      return [r.email, r.first_name ?? "", r.last_name ?? "", trackUrl, unsubUrl, pixelUrl];
    });
    const header = [
      "email",
      "first_name",
      "last_name",
      "tracking_url",
      "unsubscribe_url",
      "pixel_url",
    ];
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((v) => {
            const s = String(v ?? "");
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaign.name.replace(/[^a-z0-9]+/gi, "_")}_tracking.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const realClicks = clicks.filter((c) => !c.is_bot).length;
  const botClicks = clicks.filter((c) => c.is_bot).length;
  const ctr = recipients.length > 0 ? ((realClicks / recipients.length) * 100).toFixed(1) : "0";
  const unsubRate =
    recipients.length > 0 ? ((unsubscribes.length / recipients.length) * 100).toFixed(2) : "0";
  const botShare = clicks.length > 0 ? ((botClicks / clicks.length) * 100).toFixed(0) : "0";

  const copyLink = (token: string) => {
    navigator.clipboard.writeText(`${base}/api/public/r/${token}`);
    toast.success("Tracking-Link kopiert");
  };

  return (
    <div className="p-8 space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => nav({ to: "/outreach-tracking", search: { id: undefined } })}
      >
        <ArrowLeft className="size-4 mr-1" /> Zurück
      </Button>

      <PageHeader
        title={campaign.name}
        description={campaign.description ?? undefined}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="size-4 mr-1" /> CSV exportieren
            </Button>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[image:var(--gradient-primary)]">
                  <Upload className="size-4 mr-1" /> Empfänger importieren
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Empfänger importieren</DialogTitle>
                  <DialogDescription>
                    CSV einfügen. Pflicht-Spalte: <code>email</code>. Optional:{" "}
                    <code>first_name</code>, <code>last_name</code>, <code>target_url</code> +
                    beliebige Custom-Felder.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  rows={10}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder={
                    "email,first_name,last_name,target_url\njohn@beispiel.de,John,Doe,https://mein-angebot.de/landing"
                  }
                  className="font-mono text-xs"
                />
                {parsed && (
                  <div className="text-xs space-y-1">
                    <p className="text-emerald-600">✓ {parsed.rows.length} gültige Empfänger</p>
                    {parsed.errors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-destructive">
                        {e}
                      </p>
                    ))}
                    {parsed.errors.length > 5 && (
                      <p className="text-muted-foreground">
                        … und {parsed.errors.length - 5} weitere Fehler
                      </p>
                    )}
                  </div>
                )}
                <Button
                  onClick={() => doImport.mutate()}
                  disabled={!parsed || parsed.rows.length === 0 || doImport.isPending}
                  className="w-full"
                >
                  {doImport.isPending ? "Importiere…" : `${parsed?.rows.length ?? 0} importieren`}
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Empfänger" value={recipients.length} icon={<Users className="size-4" />} />
        <StatCard
          label="Echte Klicks"
          value={realClicks}
          subtitle={`${ctr}% CTR`}
          icon={<MousePointerClick className="size-4" />}
        />
        <StatCard
          label="Bot-Klicks"
          value={botClicks}
          subtitle={`${botShare}% aller Klicks`}
          icon={<Bot className="size-4" />}
          tone={Number(botShare) > 50 ? "warn" : "default"}
        />
        <StatCard
          label="Opens"
          value={opens.length}
          subtitle={campaign.pixel_enabled ? "Pixel an" : "Pixel aus"}
          icon={campaign.pixel_enabled ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
        />
        <StatCard
          label="Unsubscribes"
          value={unsubscribes.length}
          subtitle={`${unsubRate}%`}
          icon={<MailX className="size-4" />}
          tone={Number(unsubRate) > 0.5 ? "warn" : "default"}
        />
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm">Tracking-Pixel</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Open-Tracking ein/aus. Bei Cold Outreach empfohlen: AUS (Spam-Risiko).
            </p>
          </div>
          <Switch checked={campaign.pixel_enabled} onCheckedChange={(v) => togglePixel.mutate(v)} />
        </div>
      </Card>

      <Tabs defaultValue="recipients">
        <TabsList>
          <TabsTrigger value="recipients">Empfänger ({recipients.length})</TabsTrigger>
          <TabsTrigger value="clicks">Klicks ({clicks.length})</TabsTrigger>
          <TabsTrigger value="opens">Opens ({opens.length})</TabsTrigger>
          <TabsTrigger value="unsubs">Unsubscribes ({unsubscribes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="recipients">
          <Card className="p-0 overflow-hidden">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 text-right">Klicks</th>
                    <th className="px-3 py-2 text-right">Bots</th>
                    <th className="px-3 py-2 text-right">Opens</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground text-xs">
                        Noch keine Empfänger
                      </td>
                    </tr>
                  ) : (
                    recipients.map((r) => (
                      <tr key={r.id} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                        <td className="px-3 py-2 text-xs">
                          {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="px-3 py-2 text-right">{r.click_count}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {r.bot_click_count}
                        </td>
                        <td className="px-3 py-2 text-right">{r.open_count}</td>
                        <td className="px-3 py-2">
                          {r.unsubscribed_at ? (
                            <Badge variant="destructive" className="text-[10px]">
                              Unsubscribed
                            </Badge>
                          ) : r.click_count > 0 ? (
                            <Badge className="text-[10px]">Engaged</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px]">
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => copyLink(r.token)}>
                            <Copy className="size-3" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="clicks">
          <Card className="p-0 overflow-hidden">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Zeit</th>
                    <th className="px-3 py-2">IP</th>
                    <th className="px-3 py-2">Land</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">User-Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {clicks.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground text-xs">
                        Noch keine Klicks
                      </td>
                    </tr>
                  ) : (
                    clicks.map((c) => (
                      <tr key={c.id} className="border-t border-border">
                        <td className="px-3 py-2 text-xs">
                          {new Date(c.occurred_at).toLocaleString("de-DE")}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">{c.ip ?? "—"}</td>
                        <td className="px-3 py-2 text-xs">{c.country ?? "—"}</td>
                        <td className="px-3 py-2">
                          {c.is_bot ? (
                            <Badge variant="secondary" className="text-[10px]">
                              <Bot className="size-3 mr-1" /> Bot · {c.bot_reason}
                            </Badge>
                          ) : (
                            <Badge className="text-[10px]">Echt</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[300px]">
                          {c.user_agent ?? "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="opens">
          <Card className="p-0 overflow-hidden">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Zeit</th>
                    <th className="px-3 py-2">Provider</th>
                    <th className="px-3 py-2">Proxy</th>
                  </tr>
                </thead>
                <tbody>
                  {opens.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-muted-foreground text-xs">
                        {campaign.pixel_enabled ? "Noch keine Opens" : "Pixel ist deaktiviert"}
                      </td>
                    </tr>
                  ) : (
                    opens.map((o) => (
                      <tr key={o.id} className="border-t border-border">
                        <td className="px-3 py-2 text-xs">
                          {new Date(o.occurred_at).toLocaleString("de-DE")}
                        </td>
                        <td className="px-3 py-2 text-xs">{o.provider}</td>
                        <td className="px-3 py-2 text-xs">{o.is_proxy ? "Ja (cached)" : "Nein"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="unsubs">
          <Card className="p-0 overflow-hidden">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2">Zeit</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Methode</th>
                  </tr>
                </thead>
                <tbody>
                  {unsubscribes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-muted-foreground text-xs">
                        Noch keine Unsubscribes
                      </td>
                    </tr>
                  ) : (
                    unsubscribes.map((u) => (
                      <tr key={u.id} className="border-t border-border">
                        <td className="px-3 py-2 text-xs">
                          {new Date(u.occurred_at).toLocaleString("de-DE")}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">{u.email}</td>
                        <td className="px-3 py-2 text-xs">{u.method}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="p-4 border-destructive/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm text-destructive">Kampagne löschen</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Alle Empfänger, Klicks, Opens und Unsubscribes werden mitgelöscht. Nicht umkehrbar.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              if (confirm(`Kampagne "${campaign.name}" wirklich löschen?`)) doDelete.mutate();
            }}
          >
            <Trash2 className="size-4 mr-1" /> Löschen
          </Button>
        </div>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  subtitle,
  icon,
  tone = "default",
}: {
  label: string;
  value: number | string;
  subtitle?: string;
  icon?: React.ReactNode;
  tone?: "default" | "warn";
}) {
  return (
    <Card className={"p-4 " + (tone === "warn" ? "border-amber-500/40" : "")}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      {subtitle && (
        <div
          className={
            "text-xs mt-1 " + (tone === "warn" ? "text-amber-600" : "text-muted-foreground")
          }
        >
          {subtitle}
        </div>
      )}
    </Card>
  );
}
